import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from './local-storage.service';

export type ThemeMode = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly themeStorageKey = 'app-theme';
  private readonly theme$ = new BehaviorSubject<ThemeMode>('light');

  constructor(private localStorageService: LocalStorageService) {
    this.initTheme();
  }

  getTheme(): ThemeMode {
    return this.theme$.value;
  }

  getTheme$(): Observable<ThemeMode> {
    return this.theme$.asObservable();
  }

  setTheme(theme: ThemeMode): void {
    if (theme === this.theme$.value) {
      return;
    }
    this.theme$.next(theme);
    this.localStorageService.setLocalData(this.themeStorageKey, theme);
    this.applyTheme(theme);
  }

  toggleTheme(): ThemeMode {
    const nextTheme: ThemeMode = this.theme$.value === 'light' ? 'dark' : 'light';
    this.setTheme(nextTheme);
    return nextTheme;
  }

  private initTheme(): void {
    const stored = this.localStorageService.getLocalData(this.themeStorageKey) as ThemeMode | null;
    const theme: ThemeMode = stored === 'dark' ? 'dark' : 'light';
    this.theme$.next(theme);
    this.applyTheme(theme);
  }

  private applyTheme(theme: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.setProperty('--app-color-scheme', theme === 'dark' ? 'dark' : 'light');
  }
}
